import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSubmitTimesheetMutation } from '../../hooks/useTimesheetMutations';
import { useSearchParams } from 'react-router-dom';
import { PlusIcon, PaperAirplaneIcon, UserPlusIcon } from '@heroicons/react/24/outline';
import { TimesheetNavigation, type ViewMode } from '../../components/time/TimesheetNavigation';
import { MonthGrid, type PastedEntryData, type MonthGridHandle } from '../../components/time/MonthGrid';
import { WeekGrid } from '../../components/time/WeekGrid';
import { DayGrid } from '../../components/time/DayGrid';
import { TimeEntryList, type GroupingMode } from '../../components/time/TimeEntryList';
import { GroupingToggle } from '../../components/time/GroupingToggle';
import { EntryModal } from '../../components/time/EntryModal';
import {
  BackdatedHoursRequestModal,
  type BackdatedHoursPrefill,
} from '../../components/time/BackdatedHoursRequestModal';
import { MyBackdatedHoursRequests } from '../../components/time/MyBackdatedHoursRequests';
import { ShortcutsPopover, type Shortcut } from '../../components/time/ShortcutsPopover';
import { TimesheetSummaryPanel } from '../../components/time/TimesheetSummaryPanel';
import { MonkaConnectionBanner } from '../../components/time/MonkaConnectionBanner';
import { useMonthLockKeys } from '../../hooks/useProjectMonthLocks';
import { useFlag } from '../../services/flagsContext';
import { useTimeLoggingStatus } from '../../hooks/useBotConnection';
import { Spinner } from '../../components/common/Spinner';
import { ConfirmModal } from '../../components/common/Modal';
import { StatusBadge } from '../../components/workflow';
import { useTrackedTimeout } from '../../hooks/useTrackedTimeout';
import { StateHistory } from '../../components/workflow/StateHistory';
import { AppToast, type AppToastState } from '../../components/app-access/AppToast';
import { useAuth } from '../../services/authContext';
import { useAuditTrail } from '../../hooks/useAuditTrail';
import {
  getDateRangeEntries,
  getProjects,
  getTimesheetHistory,
  getCurrentTimesheet,
  createEntry,
  updateEntry,
  deleteEntry,
  getHolidays,
  type Entry,
} from '../../services/api/timeEntry';
import { getUserTimeOffs } from '../../services/api/timeOff';
import {
  isContractorEmployeeType,
  timeOffHoursTowardTarget,
} from '../../utils/timeOffBehaviour';
import { usePreferences, useUpdatePreferences } from '../../hooks/usePreferences';
import { isMacPlatform } from '../../utils/platform';
import { useMyJoinRequests, useCreateJoinRequest, useDeleteJoinRequest } from '../../hooks/useJoinRequests';
import { filterVisibleJoinRequests } from '../../utils/joinRequestVisibility';
import { Modal } from '../../components/common/Modal';
import { getAllProjects } from '../../services/api/timeEntry';
import { formatDisplayDate, formatLocalDate } from '../../utils/format';
import { LEGACY_CZ_DATE_FORMAT, formatMonthYear, getActiveDateFormat } from '../../utils/dateFormat';
import { DateField } from '../../components/ui/DateField';

interface LockedPeriod {
  from: string;
  to: string;
  state: string;
}

function getMonthStart(year: number, month: number): string {
  return formatLocalDate(new Date(year, month, 1));
}

function getMonthEnd(year: number, month: number): string {
  return formatLocalDate(new Date(year, month + 1, 0));
}

/**
 * Phase 28 / MKS-108 / MKS-118: build the submit-confirmation popup body.
 * Shows BOTH total and submittable hours (open + rejected — approved/submitted
 * skipped). The leading sentence makes it explicit that submitting from week
 * or day view still ships the whole monthly timesheet, because the popup is
 * the same regardless of `viewMode` and Petra found that ambiguous.
 *
 * Exported for unit testing without paying the page-level render cost.
 */
export function buildSubmitPopupMessage(entries: { state: string; hours: number }[]): string {
  const submittable = entries
    .filter((e) => e.state === 'open' || e.state === 'rejected')
    .reduce((sum, e) => sum + e.hours, 0);
  const total = entries.reduce((sum, e) => sum + e.hours, 0);
  const fmt = (n: number) =>
    n.toLocaleString('cs-CZ', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  return [
    'This submits your whole monthly timesheet — not just the visible week or day.',
    '',
    `Total in period: ${fmt(total)} h`,
    `Submitting now: ${fmt(submittable)} h (open + returned for revision)`,
    'Approved entries stay approved.',
  ].join('\n');
}

/**
 * Render the period (year/month) as a human-friendly label, e.g. "May 2026".
 * Used in the submit confirmation modal title so it's obvious which timesheet
 * is about to be sent for approval — independent of whether the user is
 * currently looking at the month, week, or day view.
 *
 * MKS-315 — the comment always claimed this label was English; the code
 * formatted it Czech. It is English now, matching the period title above the
 * grid.
 */
export function formatPeriodLabel(year: number, month: number): string {
  const monthStart = new Date(year, month, 1);
  // MKS-351's own flag is still live: while it is off the legacy spec is the
  // active one and the label is formatted here, not by `utils/dateFormat`. The
  // two are not interchangeable — `formatMonthYear` follows
  // ENGLISH_LABEL_FORMAT, which is MKS-351's spec, not this one.
  if (getActiveDateFormat() !== LEGACY_CZ_DATE_FORMAT) return formatMonthYear(monthStart);
  return monthStart.toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
  });
}

export function TimeEntryPage() {
  const queryClient = useQueryClient();
  const { user, isAdmin, isFinance } = useAuth();
  const [searchParams] = useSearchParams();
  const today = new Date();

  // Check if period is specified in URL (e.g., ?period=2026-01)
  const periodParam = searchParams.get('period');
  let initialYear = today.getFullYear();
  let initialMonth = today.getMonth();

  if (periodParam) {
    const [yearStr, monthStr] = periodParam.split('-');
    if (yearStr && monthStr) {
      const year = parseInt(yearStr, 10);
      const month = parseInt(monthStr, 10) - 1; // month is 0-indexed
      if (!isNaN(year) && !isNaN(month) && month >= 0 && month <= 11) {
        initialYear = year;
        initialMonth = month;
      }
    }
  }

  // MKS-327: contractor by `employeeType.category`, not by looking for the
  // substring "freelance" in the type's name. See `utils/timeOffBehaviour.ts`.
  const isFreelancer = isContractorEmployeeType(user?.employeeType);
  const [year, setYear] = useState(initialYear);
  const [month, setMonth] = useState(initialMonth);
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    return (sessionStorage.getItem('monksheets:viewMode') as ViewMode) || 'month';
  });
  const [weekStart, setWeekStart] = useState<Date>(() => {
    const d = new Date();
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Monday
    return new Date(d.getFullYear(), d.getMonth(), diff);
  });
  const [selectedDate, setSelectedDate] = useState<string>(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState<Entry | null>(null);
  const [newEntryDate, setNewEntryDate] = useState<string | undefined>();
  const [newEntryProjectId, setNewEntryProjectId] = useState<number | undefined>();
  const [pastedData, setPastedData] = useState<PastedEntryData | undefined>();
  // Phase 113 (MKS-208, SC3) — the backdated-hours request the entry dialog
  // hands over when a save is refused by a project-month lock.
  const [backdatedPrefill, setBackdatedPrefill] = useState<BackdatedHoursPrefill | null>(null);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [toast, setToast] = useState<AppToastState | null>(null);
  const [selectedEntryIds, setSelectedEntryIds] = useState<Set<number>>(new Set());
  const [statusFilters, setStatusFilters] = useState<Set<string>>(new Set());
  const [groupingMode, setGroupingMode] = useState<GroupingMode>(() => {
    const saved = sessionStorage.getItem('monksheets:reportedEntries:groupBy');
    return saved === 'day' ? 'day' : 'project';
  });

  // Join request state
  const [showJoinRequestModal, setShowJoinRequestModal] = useState(false);
  const [joinProjectSearch, setJoinProjectSearch] = useState('');
  const [joinSelectedProjectId, setJoinSelectedProjectId] = useState<number | null>(null);
  const [joinStartDate, setJoinStartDate] = useState('');
  const { data: myJoinRequests } = useMyJoinRequests();
  const createJoinRequestMutation = useCreateJoinRequest();
  const deleteJoinRequestMutation = useDeleteJoinRequest();

  const monthGridRef = useRef<MonthGridHandle>(null);
  // F1-010: track the modal-close refocus timeout so we cancel it on
  // unmount / before re-scheduling — prevents .focus() firing on a null
  // ref if the user navigates away within the 50ms delay.
  const refocus = useTrackedTimeout();

  // Server-backed preferences for project ordering
  const { data: preferences } = usePreferences();
  const updatePreferencesMutation = useUpdatePreferences();
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  // Local state mirrors server preferences but allows instant UI updates
  const [localProjectOrder, setLocalProjectOrder] = useState<number[]>([]);
  const [localShowAll, setLocalShowAll] = useState(false);
  const [localHiddenProjects, setLocalHiddenProjects] = useState<number[]>([]);
  const prefsLoaded = useRef(false);

  // Sync local state from server when preferences first load.
  // If server has no saved order yet, migrate from localStorage and seed the server.
  useEffect(() => {
    if (preferences && !prefsLoaded.current) {
      prefsLoaded.current = true;

      let order = preferences.projectOrder;
      let showAll = preferences.showAllProjects;
      const needsSeed = order === undefined && showAll === undefined;

      if (order === undefined) {
        try {
          const saved = localStorage.getItem('monthgrid-project-order');
          if (saved) order = JSON.parse(saved);
        } catch { /* ignore */ }
      }
      if (showAll === undefined) {
        try {
          const saved = localStorage.getItem('monthgrid-show-all-projects');
          if (saved === 'true') showAll = true;
        } catch { /* ignore */ }
      }

      setLocalProjectOrder(order ?? []);
      setLocalShowAll(showAll ?? false);
      setLocalHiddenProjects(preferences.hiddenProjects ?? []);

      // Seed server with localStorage values so future sessions are synced
      if (needsSeed && (order || showAll)) {
        updatePreferencesMutation.mutate({
          ...(order ? { projectOrder: order } : {}),
          ...(showAll ? { showAllProjects: showAll } : {}),
        });
      }
    }
  }, [preferences]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleProjectOrderChange = useCallback((order: number[]) => {
    setLocalProjectOrder(order);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      updatePreferencesMutation.mutate({ projectOrder: order });
    }, 500);
  }, [updatePreferencesMutation]);

  const handleShowAllProjectsChange = useCallback((show: boolean) => {
    setLocalShowAll(show);
    updatePreferencesMutation.mutate({ showAllProjects: show });
  }, [updatePreferencesMutation]);

  // Global hidden projects set
  const hiddenProjectIds = useMemo(
    () => new Set(localHiddenProjects),
    [localHiddenProjects]
  );

  const handleHideProject = useCallback((projectId: number) => {
    setLocalHiddenProjects((prev) => {
      if (prev.includes(projectId)) return prev;
      const next = [...prev, projectId];
      updatePreferencesMutation.mutate({ hiddenProjects: next });
      return next;
    });
  }, [updatePreferencesMutation]);

  const handleUnhideProject = useCallback((projectId: number) => {
    setLocalHiddenProjects((prev) => {
      const next = prev.filter((id) => id !== projectId);
      updatePreferencesMutation.mutate({ hiddenProjects: next });
      return next;
    });
  }, [updatePreferencesMutation]);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => clearTimeout(debounceRef.current);
  }, []);

  const monthStart = getMonthStart(year, month);
  const monthEnd = getMonthEnd(year, month);

  // Calculate previous month range
  const prevMonth = month === 0 ? 11 : month - 1;
  const prevYear = month === 0 ? year - 1 : year;
  const prevMonthStart = getMonthStart(prevYear, prevMonth);
  const prevMonthEnd = getMonthEnd(prevYear, prevMonth);

  // Fetch monthly entries
  const {
    data: entriesData,
    isLoading: isLoadingEntries,
    error: entriesError,
  } = useQuery({
    queryKey: ['monthlyEntries', monthStart, monthEnd],
    queryFn: () => getDateRangeEntries(monthStart, monthEnd),
    staleTime: 0,
    refetchOnMount: 'always',
  });

  // MKS-121: the month grid is fed by month-scoped `entries`, but a week can
  // straddle a month boundary (e.g. Jun 29 – Jul 5). Reusing month entries
  // leaves the cross-boundary days empty in Week view. Fetch the actual visible
  // week range separately so Week view is correct at month boundaries. The
  // month fetch stays month-scoped (it also feeds Costs Summary, submit totals,
  // maxWorkingHours, and the Reported Entries list).
  const weekStartStr = formatLocalDate(weekStart);
  const weekEndStr = (() => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + 6);
    return formatLocalDate(d);
  })();
  const { data: weekEntriesData } = useQuery({
    queryKey: ['weekEntries', weekStartStr, weekEndStr],
    queryFn: () => getDateRangeEntries(weekStartStr, weekEndStr),
    enabled: viewMode === 'week',
    staleTime: 0,
    refetchOnMount: 'always',
  });

  // Fetch projects (returns assigned + default projects via /time/projects)
  // Use a namespaced key so admin `useProjects()` invalidations don't evict this
  // time-scoped payload and vice-versa (the two endpoints return different shapes).
  const { data: projectsData, isLoading: isLoadingProjects } = useQuery({
    // Keyed by the viewed month so navigating to a past month re-fetches the
    // projects whose assignment was active then (assignment may have since
    // ended). `monthStart` is the reference date sent as `from`.
    queryKey: ['time-projects', monthStart],
    queryFn: () => getProjects(undefined, monthStart),
  });

  // Fetch previous month's entries to determine active projects
  const { data: prevEntriesData } = useQuery({
    queryKey: ['monthlyEntries', prevMonthStart, prevMonthEnd],
    queryFn: () => getDateRangeEntries(prevMonthStart, prevMonthEnd),
  });

  // Extract project IDs that had hours in previous month
  const previousPeriodProjectIds = useMemo(() => {
    const ids = new Set<number>();
    for (const entry of prevEntriesData?.data.entries || []) {
      if (entry.hours > 0) {
        ids.add(entry.projectId);
      }
    }
    return ids;
  }, [prevEntriesData]);

  // Fetch timesheets to determine locked periods
  const { data: timesheetsData } = useQuery({
    queryKey: ['timesheetHistory'],
    queryFn: () => getTimesheetHistory(50), // Get enough to cover past periods
  });

  // Fetch approved time-off for the viewed year
  const { data: timeOffData } = useQuery({
    queryKey: ['userTimeOffs', year, 'approved'],
    queryFn: () => getUserTimeOffs({ year, state: 'approved' }),
  });

  /**
   * Time-off days as a map of date -> hours, where the hours are what the day
   * CONTRIBUTES to the month total (which the grid compares against
   * `working days × 8`). A day that fills no target still appears, at 0 h.
   * See `timeOffHoursTowardTarget` for what zeroes it.
   */
  const timeOffDays = useMemo(() => {
    const days = new Map<string, { hours: number; typeName: string }>();
    for (const timeOff of timeOffData || []) {
      const typeName = timeOff.timeOffType?.name || 'Time Off';
      for (const day of timeOff.days) {
        if (day.date) {
          const hours = timeOffHoursTowardTarget(day.hours, timeOff.timeOffType, {
            isContractor: isFreelancer,
          });
          days.set(day.date, { hours, typeName });
        }
      }
    }
    return days;
  }, [timeOffData, isFreelancer]);

  // Fetch holidays for the year
  const { data: holidaysData } = useQuery({
    queryKey: ['holidays', year],
    queryFn: () => getHolidays(year),
    staleTime: 24 * 60 * 60 * 1000, // Cache for 24 hours
  });

  // Calculate max working hours for the month
  const maxWorkingHours = useMemo(() => {
    const holidayDates = new Set<string>();
    if (holidaysData?.data) {
      for (const holiday of holidaysData.data) {
        holidayDates.add(holiday.date);
      }
    }

    let workingDays = 0;
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const dayOfWeek = date.getDay();
      const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
      const isHoliday = holidayDates.has(dateStr);
      if (!isWeekend && !isHoliday) {
        workingDays++;
      }
    }
    return workingDays * 8;
  }, [year, month, holidaysData]);

  // Fetch current timesheet for submit functionality
  const { data: currentTimesheetData } = useQuery({
    queryKey: ['currentTimesheet'],
    queryFn: getCurrentTimesheet,
  });

  const currentTimesheet = currentTimesheetData?.data;

  // Find the timesheet for the currently viewed month (match by period name, not from date)
  const viewedPeriodName = `${year}-${String(month + 1).padStart(2, '0')}`;
  const viewedTimesheet = useMemo(() => {
    const timesheets = timesheetsData?.data || [];
    return timesheets.find((ts) => ts.period?.name === viewedPeriodName);
  }, [timesheetsData, viewedPeriodName]);

  // Audit trail for the viewed timesheet
  const { data: auditTrail } = useAuditTrail('Timesheet', viewedTimesheet?.id ?? 0);

  // Check if viewing current period (match by period name, not from date)
  const isCurrentPeriod = currentTimesheet?.period?.name === viewedPeriodName;

  // Phase 113 (MKS-208, D-13) — lock state has to be visible on the screen
  // where the hours are entered, not only on the project page. This is a
  // SECOND lookup alongside `lockedPeriods` below: that one is date-only (one
  // person's submitted timesheet), this one is per (project, month).
  const projectMonthLocks = useMonthLockKeys(year, month + 1);

  // MKS-536 — with this on, a locked project-month still lets an existing
  // entry be MOVED (another task, another day of the same month, a sibling
  // project of the same customer or another Internal one): the month's
  // reported total does not change, so it needs no backdated-hours request.
  // Adding and deleting hours still do, and that is what the three surfaces
  // below keep closed. Off = today's behaviour exactly.
  const lockedMonthMoveOn = useFlag('mks_536_locked_month_move');

  // The entry being edited sits in a locked project-month: the dialog then
  // freezes hours and the date, which are the two fields the backend will
  // still refuse.
  const editingEntryMonthLocked =
    lockedMonthMoveOn &&
    !!editingEntry &&
    projectMonthLocks.has(`${editingEntry.projectId}-${editingEntry.date.slice(0, 7)}`);

  // MKS-271 — the locked-month notice above the grid opens the same dialog the
  // refused save does, except nothing is pre-filled: the day (and the project,
  // when more than one is closed) is picked inside it.
  const handleRequestBackdatedHoursForMonth = useCallback(
    (lockedProjectIds: number[]) => {
      const firstProjectId = lockedProjectIds[0];
      if (firstProjectId === undefined) return;
      setBackdatedPrefill({
        projectId: firstProjectId,
        date: '',
        pickDate: {
          month: `${year}-${String(month + 1).padStart(2, '0')}`,
          projectIds: lockedProjectIds,
        },
      });
    },
    [year, month],
  );

  // MKS-279 — a join request decided months ago used to sit in the table below
  // forever.
  const visibleJoinRequests = useMemo(() => {
    const all = myJoinRequests ?? [];
    return filterVisibleJoinRequests(all, Date.now());
  }, [myJoinRequests]);

  // Extract locked periods (submitted, approved, or rejected timesheets)
  const lockedPeriods: LockedPeriod[] = (timesheetsData?.data || [])
    .filter((ts) => ts.state === 'submitted' || ts.state === 'approved' || ts.state === 'rejected')
    .map((ts) => ({
      from: ts.period.from,
      to: ts.period.to,
      state: ts.state,
    }));

  // Mutations
  const createMutation = useMutation({
    mutationFn: createEntry,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['monthlyEntries'] });
      queryClient.invalidateQueries({ queryKey: ['weekEntries'] });
      queryClient.invalidateQueries({ queryKey: ['managerTasks'] });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Parameters<typeof updateEntry>[1] }) =>
      updateEntry(id, data),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['monthlyEntries'] });
      queryClient.invalidateQueries({ queryKey: ['weekEntries'] });
      queryClient.invalidateQueries({ queryKey: ['managerTasks'] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteEntry,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['monthlyEntries'] });
      queryClient.invalidateQueries({ queryKey: ['weekEntries'] });
      queryClient.invalidateQueries({ queryKey: ['managerTasks'] });
    },
  });

  // Phase 28 D1 / review M-03 — share the optimistic + invalidate behaviour
  // from useSubmitTimesheetMutation so the rejected-timesheet banner clears on
  // the same code path as the manager pages refetch.
  const submitTimesheetHook = useSubmitTimesheetMutation({
    onSuccessToast: () => {
      setShowSubmitModal(false);
      setToast({ variant: 'success', message: 'Timesheet submitted for approval.' });
    },
    onErrorToast: (message) => {
      setShowSubmitModal(false);
      setToast({ variant: 'error', message: `Submit failed: ${message}` });
    },
  });
  const submitMutation = {
    isPending: submitTimesheetHook.isPending,
    mutate: () => {
      const id = isCurrentPeriod && currentTimesheet
        ? currentTimesheet.id
        : viewedTimesheet?.id;
      if (id === undefined) {
        setShowSubmitModal(false);
        setToast({
          variant: 'error',
          message: 'No timesheet to submit for this period.',
        });
        return;
      }
      submitTimesheetHook.mutate({ id });
    },
  };

  // Navigation handlers
  const handlePrevious = useCallback(() => {
    if (viewMode === 'month') {
      setMonth((prev) => {
        if (prev === 0) {
          setYear((y) => y - 1);
          return 11;
        }
        return prev - 1;
      });
    } else if (viewMode === 'week') {
      setWeekStart((prev) => {
        const d = new Date(prev);
        d.setDate(d.getDate() - 7);
        setYear(d.getFullYear());
        setMonth(d.getMonth());
        return d;
      });
    } else {
      setSelectedDate((prev) => {
        const d = new Date(prev + 'T00:00:00');
        d.setDate(d.getDate() - 1);
        setYear(d.getFullYear());
        setMonth(d.getMonth());
        const str = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        return str;
      });
    }
  }, [viewMode]);

  const handleNext = useCallback(() => {
    if (viewMode === 'month') {
      setMonth((prev) => {
        if (prev === 11) {
          setYear((y) => y + 1);
          return 0;
        }
        return prev + 1;
      });
    } else if (viewMode === 'week') {
      setWeekStart((prev) => {
        const d = new Date(prev);
        d.setDate(d.getDate() + 7);
        setYear(d.getFullYear());
        setMonth(d.getMonth());
        return d;
      });
    } else {
      setSelectedDate((prev) => {
        const d = new Date(prev + 'T00:00:00');
        d.setDate(d.getDate() + 1);
        setYear(d.getFullYear());
        setMonth(d.getMonth());
        const str = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        return str;
      });
    }
  }, [viewMode]);

  const handleToday = useCallback(() => {
    const now = new Date();
    setYear(now.getFullYear());
    setMonth(now.getMonth());
    // Also update week and day to current
    const day = now.getDay();
    const diff = now.getDate() - day + (day === 0 ? -6 : 1);
    setWeekStart(new Date(now.getFullYear(), now.getMonth(), diff));
    setSelectedDate(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`);
  }, []);

  const handleViewModeChange = useCallback((mode: ViewMode) => {
    setViewMode(mode);
    sessionStorage.setItem('monksheets:viewMode', mode);
    // Sync week/day to current month context
    if (mode === 'week') {
      const d = new Date(year, month, 1);
      const today = new Date();
      // If current month contains today, use today's week
      if (today.getFullYear() === year && today.getMonth() === month) {
        const day = today.getDay();
        const diff = today.getDate() - day + (day === 0 ? -6 : 1);
        setWeekStart(new Date(today.getFullYear(), today.getMonth(), diff));
      } else {
        // Use first Monday of the month
        const dayOfWeek = d.getDay();
        const diff = dayOfWeek === 0 ? 1 : dayOfWeek === 1 ? 0 : 8 - dayOfWeek;
        d.setDate(d.getDate() + diff);
        setWeekStart(d);
      }
    } else if (mode === 'day') {
      const today = new Date();
      if (today.getFullYear() === year && today.getMonth() === month) {
        setSelectedDate(`${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`);
      } else {
        setSelectedDate(`${year}-${String(month + 1).padStart(2, '0')}-01`);
      }
    }
  }, [year, month]);

  // Entry handlers
  const handleEditEntry = useCallback((entry: Entry) => {
    setEditingEntry(entry);
    setNewEntryDate(undefined);
    setNewEntryProjectId(undefined);
    setPastedData(undefined);
    setIsModalOpen(true);
  }, []);

  const handleCreateEntry = useCallback((date: string, projectId: number, pasted?: PastedEntryData) => {
    setEditingEntry(null);
    setNewEntryDate(date);
    setNewEntryProjectId(projectId);
    setPastedData(pasted);
    setIsModalOpen(true);
  }, []);

  const handleNewEntry = useCallback(() => {
    setEditingEntry(null);
    setNewEntryDate(formatLocalDate(new Date()));
    setNewEntryProjectId(undefined);
    setPastedData(undefined);
    setIsModalOpen(true);
  }, []);

  const handleSaveEntry = async (data: {
    projectId: number;
    date: string;
    hours: number;
    description?: string;
    task?: string;
  }) => {
    if (editingEntry) {
      await updateMutation.mutateAsync({
        id: editingEntry.id,
        data: {
          hours: data.hours,
          description: data.description,
          task: data.task,
          projectId: data.projectId,
          date: data.date,
        },
      });
    } else {
      await createMutation.mutateAsync(data);
    }
  };

  const handleMoveEntry = useCallback(
    async (entryId: number, newDate: string, newProjectId: number) => {
      // F2-006: MonthGrid/WeekGrid/DayGrid call this without awaiting, so a
      // rejected mutateAsync previously surfaced only as an
      // unhandled-rejection. The optimistic update inside useUpdateEntry
      // rolls the cache back on error, but the user got no toast. Swallow
      // the rejection here and surface it via the existing AppToast so the
      // drag-drop UX has the same error feedback as button-click handlers.
      try {
        await updateMutation.mutateAsync({
          id: entryId,
          data: {
            date: newDate,
            projectId: newProjectId,
          },
        });
      } catch (err) {
        setToast({
          variant: 'error',
          message: `Failed to move entry: ${(err as Error).message || 'unknown error'}`,
        });
      }
    },
    [updateMutation]
  );

  const handleDeleteEntry = async () => {
    if (editingEntry) {
      await deleteMutation.mutateAsync(editingEntry.id);
    }
  };

  const handleDeleteEntryDirect = useCallback((entry: Entry) => {
    deleteMutation.mutate(entry.id);
  }, [deleteMutation]);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setEditingEntry(null);
    setNewEntryDate(undefined);
    setNewEntryProjectId(undefined);
    setPastedData(undefined);
    // Refocus grid after modal closes
    refocus.schedule(() => {
      monthGridRef.current?.focus();
    }, 50);
  }, [refocus]);

  // MKS-315 — the list behind the Shortcuts popover. Same keys the handler
  // below and the grids bind; kept next to them so they stay in step.
  const shortcuts: Shortcut[] = useMemo(
    () => [
      {
        keys: 'Shift+←/→',
        action: `Navigate ${viewMode === 'month' ? 'months' : viewMode === 'week' ? 'weeks' : 'days'}`,
      },
      { keys: 'Shift+T', action: 'Today' },
      { keys: 'Shift+N', action: 'New entry' },
      { keys: 'Shift+↑/↓', action: 'Move row' },
      { keys: 'D', action: 'Delete entry' },
      { keys: `${isMacPlatform() ? '⌘' : 'Ctrl'}+C / +V`, action: 'Copy / paste cell' },
    ],
    [viewMode]
  );

  // Keyboard navigation (Shift+key to avoid browser shortcut conflicts)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Skip if user is typing in an input field
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
        return;
      }

      if (e.key === 'ArrowLeft' && e.shiftKey && !e.ctrlKey && !e.altKey && !e.metaKey) {
        e.preventDefault();
        handlePrevious();
      } else if (e.key === 'ArrowRight' && e.shiftKey && !e.ctrlKey && !e.altKey && !e.metaKey) {
        e.preventDefault();
        handleNext();
      } else if (e.key === 'T' && e.shiftKey && !e.ctrlKey && !e.altKey && !e.metaKey) {
        e.preventDefault();
        handleToday();
      } else if (e.key === 'N' && e.shiftKey && !e.ctrlKey && !e.altKey && !e.metaKey) {
        e.preventDefault();
        handleNewEntry();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handlePrevious, handleNext, handleToday, handleNewEntry]);

  const isLoading = isLoadingEntries || isLoadingProjects;
  const entries = useMemo(() => entriesData?.data.entries || [], [entriesData?.data.entries]);
  const projects = projectsData?.data || [];

  // Filter entries by status (if filters are selected)
  const filteredEntries = useMemo(() => {
    if (statusFilters.size === 0) return entries; // Show all if no filter
    return entries.filter((e) => statusFilters.has(e.state));
  }, [entries, statusFilters]);

  // Calculate hours totals
  const totalEntryHours = useMemo(() => entries.reduce((sum, e) => sum + e.hours, 0), [entries]);
  const filteredHours = useMemo(() => filteredEntries.reduce((sum, e) => sum + e.hours, 0), [filteredEntries]);

  // Week view data
  const weekDates = useMemo(() => {
    const dates: string[] = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(weekStart);
      d.setDate(d.getDate() + i);
      dates.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`);
    }
    return dates;
  }, [weekStart]);

  const weekEntries = useMemo(() => {
    // MKS-121: use the dedicated week-range fetch (spans month boundaries),
    // not the month-scoped `entries`. Filter by weekDates as a guard.
    const source = weekEntriesData?.data.entries ?? [];
    const dateSet = new Set(weekDates);
    return source.filter((e) => dateSet.has(e.date));
  }, [weekEntriesData, weekDates]);

  const weekDailyTotals = useMemo(() => {
    const totals: Record<string, number> = {};
    for (const e of weekEntries) {
      totals[e.date] = (totals[e.date] || 0) + e.hours;
    }
    for (const date of weekDates) {
      const info = timeOffDays.get(date);
      if (info) totals[date] = (totals[date] || 0) + info.hours;
    }
    return totals;
  }, [weekEntries, weekDates, timeOffDays]);

  const weekTotal = useMemo(() => {
    return Object.values(weekDailyTotals).reduce((sum, h) => sum + h, 0);
  }, [weekDailyTotals]);

  // Expected (target) working hours for the displayed week: weekdays × 8,
  // minus public holidays — mirrors the month-grid denominator so the week
  // footer can show "logged / expected" (e.g. 25 / 40, or 25 / 32 with a holiday).
  const weekExpectedHours = useMemo(() => {
    const holidayDates = new Set<string>();
    if (holidaysData?.data) {
      for (const holiday of holidaysData.data) {
        holidayDates.add(holiday.date);
      }
    }
    let workingDays = 0;
    for (const date of weekDates) {
      const [y, m, d] = date.split('-').map(Number);
      const dayOfWeek = new Date(y ?? 0, (m ?? 1) - 1, d ?? 1).getDay();
      const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
      if (!isWeekend && !holidayDates.has(date)) workingDays++;
    }
    return workingDays * 8;
  }, [weekDates, holidaysData]);

  // Day view data
  const dayEntries = useMemo(() => {
    return entries.filter((e) => e.date === selectedDate);
  }, [entries, selectedDate]);

  // Toggle grouping mode and persist to sessionStorage
  const handleGroupingModeChange = useCallback((mode: GroupingMode) => {
    setGroupingMode(mode);
    sessionStorage.setItem('monksheets:reportedEntries:groupBy', mode);
  }, []);

  // Toggle status filter
  const toggleStatusFilter = (status: string) => {
    setStatusFilters((prev) => {
      const next = new Set(prev);
      if (next.has(status)) {
        next.delete(status);
      } else {
        next.add(status);
      }
      return next;
    });
  };

  // Selection handlers for bulk operations
  const selectableEntries = useMemo(() => {
    return entries.filter((e) => e.state === 'open' || e.state === 'rejected');
  }, [entries]);

  const toggleEntry = useCallback((entryId: number) => {
    const newSelected = new Set(selectedEntryIds);
    if (newSelected.has(entryId)) {
      newSelected.delete(entryId);
    } else {
      // Only add if it's selectable (open state)
      if (selectableEntries.some((e) => e.id === entryId)) {
        newSelected.add(entryId);
      }
    }
    setSelectedEntryIds(newSelected);
  }, [selectedEntryIds, selectableEntries]);

  const toggleAll = useCallback(() => {
    if (selectedEntryIds.size === selectableEntries.length) {
      setSelectedEntryIds(new Set());
    } else {
      setSelectedEntryIds(new Set(selectableEntries.map((e) => e.id)));
    }
  }, [selectedEntryIds, selectableEntries]);

  const handleBulkDelete = useCallback(async () => {
    const idsToDelete = Array.from(selectedEntryIds);
    const failedIds: number[] = [];
    // Delete entries sequentially
    for (const id of idsToDelete) {
      try {
        await deleteMutation.mutateAsync(id);
      } catch {
        failedIds.push(id);
      }
    }
    setSelectedEntryIds(new Set(failedIds));
  }, [selectedEntryIds, deleteMutation]);

  // Join request handlers
  const [allProjects, setAllProjects] = useState<{ id: number; name: string; fullName: string | null }[]>([]);

  // Load unassigned projects every time modal opens
  useEffect(() => {
    if (showJoinRequestModal) {
      getAllProjects().then(res => {
        setAllProjects(res.data.map(p => ({ id: p.id, name: p.name, fullName: p.fullName })));
      }).catch(() => {});
    } else {
      setAllProjects([]);
    }
  }, [showJoinRequestModal]);

  // Filter projects by search input (backend already excludes assigned projects)
  const filteredJoinProjects = useMemo(() => {
    if (!joinProjectSearch) return allProjects;
    const lower = joinProjectSearch.toLowerCase();
    return allProjects.filter(p =>
      (p.fullName || p.name).toLowerCase().includes(lower)
    );
  }, [allProjects, joinProjectSearch]);

  const handleCreateJoinRequest = useCallback(async () => {
    if (!joinSelectedProjectId || !joinStartDate) return;
    try {
      await createJoinRequestMutation.mutateAsync({
        projectId: joinSelectedProjectId,
        requestedStartDate: joinStartDate,
      });
      setShowJoinRequestModal(false);
      setJoinProjectSearch('');
      setJoinSelectedProjectId(null);
      setJoinStartDate('');
    } catch {
      // Error is handled by mutation state
    }
  }, [joinSelectedProjectId, joinStartDate, createJoinRequestMutation]);

  // Can submit if:
  // 1. Viewing a period with no timesheet yet (user added entries but hasn't created timesheet)
  // 2. Viewing a period with open/rejected timesheet
  const canSubmit = (!viewedTimesheet && entries.length > 0) ||
    (viewedTimesheet && (viewedTimesheet.state === 'open' || viewedTimesheet.state === 'rejected'));

  // Period is locked when timesheet is submitted or approved
  const isPeriodLocked = viewedTimesheet?.state === 'submitted' || viewedTimesheet?.state === 'approved';

  // MKS-388 — a Claude seat holder with no connected Monka cannot write time
  // at all (the backend refuses POST/PATCH with 403
  // `MONKA_CONNECTION_REQUIRED`), so the page says so up front and takes the
  // write controls away instead of letting them type into a refusal. Reads
  // stay live: viewing, deleting and submitting an already-filled timesheet
  // are all still allowed, so `readOnly` — which also kills delete — is
  // deliberately NOT what this sets.
  const { data: timeLoggingStatus } = useTimeLoggingStatus();
  const monkaBlocked = timeLoggingStatus?.blocked === true;
  const MONKA_SAVE_DISABLED_REASON = 'Connect Monka to log time.';

  // Phase 28 / MKS-113 — surface rejection metadata at the top of the page when
  // the viewed timesheet was returned for revision. UX-BRIEF S3 banner.
  const rejectedBanner =
    viewedTimesheet?.state === 'rejected' && viewedTimesheet.rejectionReason
      ? {
          reason: viewedTimesheet.rejectionReason,
          // MKS-351 — dd.mm.yyyy from the shared util.
          when: viewedTimesheet.rejectedAt ? formatDisplayDate(viewedTimesheet.rejectedAt) : null,
        }
      : null;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h1 className="text-xl font-bold text-gray-900">Timesheets</h1>
          {viewedTimesheet ? (
            <StatusBadge state={viewedTimesheet.state} />
          ) : entries.length > 0 ? (
            <StatusBadge state="open" />
          ) : null}
        </div>
        <div className="flex items-center gap-2">
          {canSubmit && (
            <button
              onClick={() => setShowSubmitModal(true)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors"
            >
              <PaperAirplaneIcon className="w-4 h-4" />
              Submit for Approval
            </button>
          )}
          {(!viewedTimesheet || viewedTimesheet.state === 'open' || viewedTimesheet.state === 'rejected') && (
            <button
              onClick={handleNewEntry}
              // MKS-388 — dead while the Monka gate refuses this user; the
              // banner below carries the reason and the fix.
              disabled={monkaBlocked}
              title={monkaBlocked ? MONKA_SAVE_DISABLED_REASON : undefined}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-primary-600 transition-colors"
            >
              <PlusIcon className="w-4 h-4" />
              New Entry
            </button>
          )}
          <button
            onClick={() => setShowJoinRequestModal(true)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <UserPlusIcon className="w-4 h-4" />
            Join Project
          </button>
          <ShortcutsPopover shortcuts={shortcuts} />
        </div>
      </div>

      {/* MKS-388 — above the entry form, persistent for as long as the gate
          refuses this user, not only after a failed save. */}
      {monkaBlocked && <MonkaConnectionBanner />}

      {rejectedBanner && (
        <div
          role="alert"
          className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
        >
          <div className="font-semibold">
            Výkaz vrácen k přepracování
            {rejectedBanner.when ? ` — ${rejectedBanner.when}` : ''}
          </div>
          <div className="mt-1 whitespace-pre-wrap">{rejectedBanner.reason}</div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <TimesheetNavigation
          year={year}
          month={month}
          viewMode={viewMode}
          weekStart={weekStart}
          selectedDate={selectedDate}
          onPrevious={handlePrevious}
          onNext={handleNext}
          onToday={handleToday}
          onViewModeChange={handleViewModeChange}
        />

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Spinner size="lg" />
          </div>
        ) : entriesError ? (
          <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm">
            Failed to load entries. Please try again.
          </div>
        ) : viewMode === 'month' ? (
          <MonthGrid
            ref={monthGridRef}
            year={year}
            month={month}
            entries={entries}
            projects={projects}
            lockedPeriods={lockedPeriods}
            projectMonthLocks={projectMonthLocks}
            previousPeriodProjectIds={previousPeriodProjectIds}
            timeOffDays={timeOffDays}
            readOnly={isPeriodLocked}
            onEditEntry={handleEditEntry}
            onDeleteEntry={handleDeleteEntryDirect}
            onCreateEntry={handleCreateEntry}
            // MKS-388 — a drag writes the entry straight away, with no dialog
            // to explain the refusal, so the gate withholds the handler
            // entirely; the grids treat an absent one as "not offered". A
            // cell click still opens the dialog, where Save is disabled and
            // says why.
            onMoveEntry={monkaBlocked ? undefined : handleMoveEntry}
            projectOrder={localProjectOrder}
            onProjectOrderChange={handleProjectOrderChange}
            showAllProjects={localShowAll}
            onShowAllProjectsChange={handleShowAllProjectsChange}
            hiddenProjectIds={hiddenProjectIds}
            onHideProject={handleHideProject}
            onUnhideProject={handleUnhideProject}
            onRequestBackdatedHours={handleRequestBackdatedHoursForMonth}
            lockedMonthMoveEnabled={lockedMonthMoveOn}
          />
        ) : viewMode === 'week' ? (
          <WeekGrid
            weekStart={weekStart}
            entries={weekEntries}
            projects={projects}
            dailyTotals={weekDailyTotals}
            weekTotal={weekTotal}
            weekExpectedHours={weekExpectedHours}
            timeOffDays={timeOffDays}
            showAllProjects={localShowAll}
            onShowAllProjectsChange={handleShowAllProjectsChange}
            previousPeriodProjectIds={previousPeriodProjectIds}
            projectOrder={localProjectOrder}
            onProjectOrderChange={handleProjectOrderChange}
            hiddenProjectIds={hiddenProjectIds}
            onHideProject={handleHideProject}
            onUnhideProject={handleUnhideProject}
            lockedPeriods={lockedPeriods}
            readOnly={isPeriodLocked}
            onEditEntry={handleEditEntry}
            onDeleteEntry={handleDeleteEntryDirect}
            onCreateEntry={handleCreateEntry}
            // MKS-388 — a drag writes the entry straight away, with no dialog
            // to explain the refusal, so the gate withholds the handler
            // entirely; the grids treat an absent one as "not offered". A
            // cell click still opens the dialog, where Save is disabled and
            // says why.
            onMoveEntry={monkaBlocked ? undefined : handleMoveEntry}
          />
        ) : (
          <DayGrid
            date={selectedDate}
            entries={dayEntries}
            projects={projects}
            timeOffDays={timeOffDays}
            showAllProjects={localShowAll}
            onShowAllProjectsChange={handleShowAllProjectsChange}
            previousPeriodProjectIds={previousPeriodProjectIds}
            projectOrder={localProjectOrder}
            onProjectOrderChange={handleProjectOrderChange}
            hiddenProjectIds={hiddenProjectIds}
            onHideProject={handleHideProject}
            onUnhideProject={handleUnhideProject}
            lockedPeriods={lockedPeriods}
            readOnly={isPeriodLocked}
            onEditEntry={handleEditEntry}
            onDeleteEntry={handleDeleteEntryDirect}
            onCreateEntry={handleCreateEntry}
            // MKS-388 — a drag writes the entry straight away, with no dialog
            // to explain the refusal, so the gate withholds the handler
            // entirely; the grids treat an absent one as "not offered". A
            // cell click still opens the dialog, where Save is disabled and
            // says why.
            onMoveEntry={monkaBlocked ? undefined : handleMoveEntry}
          />
        )}
      </div>

      {/* Summary panel under the grid (MKS-315: costs only for the people who
          act on them — admin, finance, and freelancers who invoice them). */}
      {entries.length > 0 && (
        <TimesheetSummaryPanel
          entries={entries}
          maxWorkingHours={maxWorkingHours}
          canSeeCosts={isAdmin || isFinance || isFreelancer}
          // MKS-314 follow-up — the viewed month, so the panel's costs convert
          // at THAT month's rate rather than at the newest one in force.
          // `month` is 0-indexed React state; the rate key is 1-indexed.
          month={`${year}-${String(month + 1).padStart(2, '0')}`}
        />
      )}

      {/* Entry List */}
      {entries.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <h2 className="text-sm font-semibold text-gray-900">Reported Entries</h2>
                <GroupingToggle value={groupingMode} onChange={handleGroupingModeChange} />
              </div>
              <div className="flex items-center gap-4">
                <span className="text-sm text-gray-500">
                  {filteredHours.toFixed(1)} of {totalEntryHours.toFixed(1)} hours
                  {statusFilters.size > 0 && ` (filtered)`}
                </span>
                {selectedEntryIds.size > 0 && (
                  <button
                    onClick={handleBulkDelete}
                    disabled={deleteMutation.isPending}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-red-500 hover:bg-red-600 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {deleteMutation.isPending ? 'Deleting...' : `Delete ${selectedEntryIds.size} ${selectedEntryIds.size === 1 ? 'entry' : 'entries'}`}
                  </button>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 mt-3">
              <span className="text-xs text-gray-500 mr-1">Filter by status:</span>
              {['open', 'submitted', 'approved', 'rejected'].map((status) => (
                <button
                  key={status}
                  onClick={() => toggleStatusFilter(status)}
                  className={`px-2.5 py-1 text-xs font-medium rounded-full transition-colors ${
                    statusFilters.has(status)
                      ? status === 'approved'
                        ? 'bg-green-100 text-green-700 ring-1 ring-green-500'
                        : status === 'submitted'
                          ? 'bg-yellow-100 text-yellow-700 ring-1 ring-yellow-500'
                          : status === 'rejected'
                            ? 'bg-red-100 text-red-700 ring-1 ring-red-500'
                            : 'bg-gray-200 text-gray-700 ring-1 ring-gray-500'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {status}
                </button>
              ))}
              {statusFilters.size > 0 && (
                <button
                  onClick={() => setStatusFilters(new Set())}
                  className="px-2.5 py-1 text-xs text-gray-500 hover:text-gray-700"
                >
                  Clear
                </button>
              )}
            </div>
          </div>
          <TimeEntryList
            entries={filteredEntries}
            selectableEntries={selectableEntries}
            selectedEntryIds={selectedEntryIds}
            onToggleEntry={toggleEntry}
            onToggleAll={toggleAll}
            onEditEntry={handleEditEntry}
            readOnly={false}
            groupingMode={groupingMode}
          />
        </div>
      )}

      <EntryModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSave={handleSaveEntry}
        onDelete={editingEntry ? handleDeleteEntry : undefined}
        entry={editingEntry}
        date={newEntryDate}
        projectId={newEntryProjectId}
        projects={projects}
        initialHours={pastedData?.hours}
        initialDescription={pastedData?.description}
        initialTask={pastedData?.task}
        // MKS-388 — Save is dead while the gate refuses; Delete stays live.
        saveDisabled={monkaBlocked}
        saveDisabledReason={MONKA_SAVE_DISABLED_REASON}
        // MKS-536 — the move is allowed inside a locked month; the hours and
        // the day are not.
        lockedMonthMoveOnly={editingEntryMonthLocked}
        onRequestBackdatedHours={(prefill) => {
          handleCloseModal();
          setBackdatedPrefill(prefill);
        }}
      />

      {/* Phase 113 (MKS-208, SC3) — filing the request from the point of
          refusal, pre-filled with whatever the entry dialog already had. */}
      <BackdatedHoursRequestModal
        isOpen={!!backdatedPrefill}
        onClose={() => setBackdatedPrefill(null)}
        prefill={backdatedPrefill}
        projects={projects}
        onSubmitted={() =>
          setToast({
            variant: 'success',
            message: 'Request sent — the project manager will review it.',
          })
        }
      />

      {/* Submit Confirmation Modal — Phase 28 / MKS-108 / MKS-118: title names
          the period (e.g. "Submit timesheet for May 2026") so it's obvious
          what's being shipped, and the body explicitly notes that submitting
          from week / day view still ships the whole monthly timesheet. Math
          extracted to buildSubmitPopupMessage() for unit testing. */}
      <ConfirmModal
        isOpen={showSubmitModal}
        onClose={() => setShowSubmitModal(false)}
        onConfirm={() => submitMutation.mutate()}
        title={`Submit timesheet for ${formatPeriodLabel(year, month)}`}
        message={buildSubmitPopupMessage(entries)}
        confirmText="Submit for approval"
        cancelText="Cancel"
        variant="primary"
        isLoading={submitMutation.isPending}
      />

      {/* Join Request Modal */}
      <Modal
        isOpen={showJoinRequestModal}
        onClose={() => {
          setShowJoinRequestModal(false);
          setJoinProjectSearch('');
          setJoinSelectedProjectId(null);
          setJoinStartDate('');
            }}
        title="Request to Join Project"
      >
        <div className="space-y-4">
          {/* Pending requests info */}
          {myJoinRequests && myJoinRequests.filter(r => r.state === 'pending').length > 0 && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800">
              You have {myJoinRequests.filter(r => r.state === 'pending').length} pending request(s):
              {myJoinRequests.filter(r => r.state === 'pending').map(r => (
                <span key={r.id} className="ml-1 font-medium">{r.projectName}</span>
              ))}
            </div>
          )}

          {/* Project search */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Project</label>
            <input
              type="text"
              value={joinProjectSearch}
              onChange={(e) => setJoinProjectSearch(e.target.value)}
              placeholder="Search for a project..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            {filteredJoinProjects.length > 0 && !joinSelectedProjectId && (
              <div className="mt-1 max-h-40 overflow-y-auto border border-gray-200 rounded-lg bg-white">
                {filteredJoinProjects.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => {
                      setJoinSelectedProjectId(p.id);
                      setJoinProjectSearch(p.fullName || p.name);
                                    }}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 border-b border-gray-100 last:border-b-0"
                  >
                    {p.fullName || p.name}
                  </button>
                ))}
              </div>
            )}
            {joinSelectedProjectId && (
              <button
                onClick={() => {
                  setJoinSelectedProjectId(null);
                  setJoinProjectSearch('');
                }}
                className="mt-1 text-xs text-blue-600 hover:text-blue-800"
              >
                Change project
              </button>
            )}
          </div>

          {/* Start date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Requested Start Date</label>
            <DateField
              value={joinStartDate}
              onChange={setJoinStartDate}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Error message */}
          {createJoinRequestMutation.isError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
              {(createJoinRequestMutation.error as Error)?.message || 'Failed to create request'}
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-2">
            <button
              onClick={() => {
                setShowJoinRequestModal(false);
                setJoinProjectSearch('');
                setJoinSelectedProjectId(null);
                setJoinStartDate('');
                        }}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleCreateJoinRequest}
              disabled={!joinSelectedProjectId || !joinStartDate || createJoinRequestMutation.isPending}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {createJoinRequestMutation.isPending ? 'Submitting...' : 'Submit Request'}
            </button>
          </div>
        </div>
      </Modal>

      {/* My Join Requests */}
      {visibleJoinRequests.length > 0 && (
        <div className="mt-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">My Project Join Requests</h3>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Project</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Start Date</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Requested</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Details</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {visibleJoinRequests.map((req) => (
                  <tr key={req.id} className="hover:bg-gray-50">
                    <td className="px-4 py-2 text-sm font-medium text-gray-900">{req.projectName}</td>
                    <td className="px-4 py-2 text-sm text-gray-500">
                      {formatDisplayDate(req.requestedStartDate)}
                    </td>
                    <td className="px-4 py-2">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                        req.state === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                        req.state === 'approved' ? 'bg-green-100 text-green-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {req.state}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-500">
                      {formatDisplayDate(req.createdAt)}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-500">
                      {req.state === 'declined' && req.declineReason && (
                        <span className="text-red-600" title={req.declineReason}>
                          Reason: {req.declineReason}
                        </span>
                      )}
                      {req.state === 'approved' && (
                        <span className="text-green-600">Assignment created</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-sm">
                      {req.state === 'pending' && (
                        <button
                          onClick={() => deleteJoinRequestMutation.mutate(req.id)}
                          disabled={deleteJoinRequestMutation.isPending}
                          className="text-red-600 hover:text-red-800 text-xs font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {deleteJoinRequestMutation.isPending ? 'Cancelling...' : 'Cancel'}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* MKS-265 — My Backdated Hours Requests */}
      <MyBackdatedHoursRequests />

      {/* History */}
      {viewedTimesheet && (
        <div className="mt-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">History</h3>
          <StateHistory history={auditTrail?.data ?? []} />
        </div>
      )}

      <AppToast toast={toast} onDismiss={() => setToast(null)} />
    </div>
  );
}
